import React, { useState, useEffect } from 'react';
import { DialogTitle, DialogContent, DialogActions, Stack, Box, Typography, Divider } from '@mui/material';
import { ShButton, ShTextFieldV2 } from 'shared/SharedStyles/ShInputs';
import { ShReactQuill } from 'shared/SharedComponents/ShReactQuill/ShReactQuill';
import { EmailCampaignDialogValues } from './EmailCampaignModels';
import { ShDialog } from 'shared/SharedStyles/ShFeedback';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';

interface EmailCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: EmailCampaignDialogValues) => void;
  initialValues?: EmailCampaignDialogValues;
  loading?: boolean;
}

export const EmailCampaignDialog: React.FC<EmailCampaignDialogProps> = ({ open, onClose, onSave, initialValues, loading }) => {
  const [values, setValues] = useState<EmailCampaignDialogValues>(
    initialValues || {
      title: '',
      to: [],
      subject: '',
      preview: '',
      fromEmail: '',
      replyTo: '',
      bcc: [],
      cc: [],
      emailBody: '',
    }
  );
  const [touched, setTouched] = useState<{ subject: boolean; emailBody: boolean }>({ subject: false, emailBody: false });

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues);
    } else if (!open) {
      // Reset to empty when dialog closes and not editing
      setValues({
        title: '',
        to: [],
        subject: '',
        preview: '',
        fromEmail: '',
        replyTo: '',
        bcc: [],
        cc: [],
        emailBody: '',
      });
    }
  }, [initialValues, open]);

  const handleChange = (field: keyof EmailCampaignDialogValues, value: string | string[]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuillChange = (val: React.SetStateAction<string>) => {
    const resolved = typeof val === 'function' ? (val as (prev: string) => string)(values.emailBody) : val;
    handleChange('emailBody', resolved);
  };

  const handleSave = () => {
    setTouched({ subject: true, emailBody: true });
    if (!values.subject || !values.emailBody) return;

    const cleanValues = {
      ...values,
      fromEmail: 'NA',
      bcc: Array.isArray(values.bcc) ? values.bcc.flat().filter(Boolean) : [],
      cc: Array.isArray(values.cc) ? values.cc.flat().filter(Boolean) : [],
      to: Array.isArray(values.to) ? values.to.flat().filter(Boolean) : [],
    };
    onSave(cleanValues);
  };

  return (
    <ShDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Campaign</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <ShTextFieldV2
            label="Title"
            value={values.title}
            onChange={e => handleChange('title', e.target.value)}
            fullWidth
            required
          />
          <ShTextFieldV2
            label="To"
            value={Array.isArray(values.to) ? values.to.join(', ') : ''}
            onChange={e => handleChange('to', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
            fullWidth
            placeholder="e.g. All Candidates, or tags (Coming soon)"
            disabled
          />
          <ShTextFieldV2
            label="Subject Line"
            value={values.subject}
            onChange={e => {
              handleChange('subject', e.target.value);
              if (touched.subject) setTouched(t => ({ ...t, subject: false }));
            }}
            fullWidth
            required
            error={touched.subject && !values.subject}
            helperText={touched.subject && !values.subject ? 'Subject is required' : ''}
          />
          <ShTextFieldV2
            label="Preview Text"
            value={values.preview}
            onChange={e => handleChange('preview', e.target.value)}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <ShTextFieldV2
              label="Reply To"
              value={values.replyTo}
              onChange={e => handleChange('replyTo', e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <ShTextFieldV2
              label="BCC"
              value={Array.isArray(values.bcc) ? values.bcc.join(', ') : ''}
              onChange={e => handleChange('bcc', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              fullWidth
            />
            <Box width={1}>
              <ShTextFieldV2
                label="CC"
                value={Array.isArray(values.cc) ? values.cc.join(', ') : ''}
                onChange={e => handleChange('cc', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                fullWidth
              />
              <Typography variant="caption" color="warning.main" mt={0.5}>
                Warning: You will receive a separate email for every applicant if you use CC.
              </Typography>
            </Box>
          </Stack>
          <Divider flexItem />
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Email Body:
            </Typography>
            <ShReactQuill
              quillEditorValue={values.emailBody}
              setQuillEditorValue={val => {
                handleQuillChange(val);
                if (touched.emailBody) setTouched(t => ({ ...t, emailBody: false }));
              }}
            />
            {touched.emailBody && !values.emailBody && (
              <Typography variant="caption" color="error.main">Email body is required</Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <ShButton onClick={onClose} startIcon={<CloseIcon />}>Cancel</ShButton>
        <ShButton variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />} disabled={loading || !values.subject || !values.emailBody}>
          {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
          Save Campaign
        </ShButton>
      </DialogActions>
    </ShDialog>
  );
}; 